import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { ChevronLeft, ChevronRight, Search, BookOpen } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AlQuranPage() {
  const [surahs, setSurahs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [ayahs, setAyahs] = useState([]);
  const [terjemah, setTerjemah] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showTerjemah, setShowTerjemah] = useState(true);

  useEffect(() => {
    axios.get(`${API}/quran/surah`)
      .then(r => { if (r.data.data) setSurahs(r.data.data); })
      .catch(() => {});
  }, []);

  const openSurah = async (surah) => {
    setSelected(surah);
    setLoading(true);
    try {
      const [arRes, idRes] = await Promise.all([
        axios.get(`${API}/quran/surah/${surah.number}`),
        axios.get(`${API}/quran/surah/${surah.number}/terjemah`),
      ]);
      setAyahs(arRes.data.data?.ayahs || []);
      setTerjemah(idRes.data.data?.ayahs || []);
    } catch { setAyahs([]); setTerjemah([]); }
    finally { setLoading(false); }
  };

  const filtered = surahs.filter(s =>
    s.englishName?.toLowerCase().includes(search.toLowerCase()) ||
    s.name?.includes(search) ||
    s.number?.toString() === search
  );

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        <AnimatePresence mode="wait">
          {selected ? (
            <motion.div key="surah" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}>
              <div className="bg-gradient-to-br from-emerald-700 to-emerald-900 px-6 pt-12 pb-6">
                <button onClick={() => setSelected(null)} data-testid="quran-back-btn"
                  className="flex items-center gap-1.5 text-emerald-200 text-sm mb-4">
                  <ChevronLeft size={16} /> Daftar Surah
                </button>
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="font-playfair text-xl font-bold text-white">{selected.englishName}</h1>
                    <p className="text-emerald-300 text-xs">{selected.numberOfAyahs} ayat · {selected.revelationType === 'Meccan' ? 'Makkiyah' : 'Madaniyah'}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-amiri text-3xl text-gold-300">{selected.name}</p>
                  </div>
                </div>
                <button onClick={() => setShowTerjemah(s => !s)}
                  className="mt-3 text-xs bg-white/10 text-white px-3 py-1.5 rounded-full">
                  {showTerjemah ? 'Sembunyikan Terjemah' : 'Tampilkan Terjemah'}
                </button>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <div className="p-4 space-y-4">
                  {ayahs.map((ayah, i) => (
                    <motion.div key={ayah.numberInSurah}
                      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
                      className="bg-white rounded-2xl p-4 shadow-card"
                      data-testid={`ayah-${ayah.numberInSurah}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-7 h-7 rounded-lg bg-emerald-500 flex items-center justify-center">
                          <span className="text-white text-xs font-bold">{ayah.numberInSurah}</span>
                        </div>
                      </div>
                      <p className="font-amiri text-right text-2xl text-gray-800 leading-loose mb-3" dir="rtl" lang="ar">
                        {ayah.text}
                      </p>
                      {showTerjemah && terjemah[i] && (
                        <p className="text-sm text-gray-600 leading-relaxed border-t border-gray-50 pt-3 mt-2">
                          {terjemah[i].text}
                        </p>
                      )}
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div key="list" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="bg-gradient-to-br from-emerald-700 to-emerald-900 px-6 pt-12 pb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-2xl bg-white/10 flex items-center justify-center">
                    <BookOpen size={20} className="text-white" />
                  </div>
                  <div>
                    <h1 className="font-playfair text-xl font-bold text-white">Al-Quran Digital</h1>
                    <p className="text-emerald-300 text-xs">Terjemah Kemenag RI</p>
                  </div>
                </div>
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-400" />
                  <input data-testid="quran-search"
                    value={search} onChange={e => setSearch(e.target.value)}
                    placeholder="Cari nama surah..."
                    className="w-full pl-9 pr-4 py-2.5 bg-white/10 text-white placeholder-emerald-300 rounded-xl text-sm border border-white/20 focus:outline-none focus:ring-2 focus:ring-white/30"
                  />
                </div>
              </div>
              <div className="p-4 space-y-2">
                {filtered.map((s, i) => (
                  <motion.button key={s.number} data-testid={`surah-${s.number}`}
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.01 }}
                    onClick={() => openSurah(s)}
                    className="w-full flex items-center gap-3 bg-white rounded-2xl p-4 shadow-card hover:shadow-emerald hover:-translate-y-0.5 transition-all duration-200 text-left">
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                      <span className="text-emerald-700 font-bold text-xs">{s.number}</span>
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800 text-sm">{s.englishName}</p>
                      <p className="text-xs text-gray-400">{s.numberOfAyahs} ayat · {s.revelationType === 'Meccan' ? 'Makkiyah' : 'Madaniyah'}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-amiri text-xl text-gray-600">{s.name}</p>
                    </div>
                  </motion.button>
                ))}
                {surahs.length === 0 && (
                  <div className="text-center py-10 text-gray-400 text-sm">
                    Memuat daftar surah...
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Layout>
  );
}
